import { useEffect, useRef } from "react";
import { useSearchParams } from "react-router";
import { MODELS, MUTATIONS, useMultiModel, useMutation } from "../../api";
import { ToastProvider } from "../../design-system/primitives/components/Toast/Toast";
import type { FeedbackAnswers } from "../../api/feedback";
import type { WorkshopStepInfo } from "../../api/workshop";
import { FeedbackLinkToast } from "./FeedbackLinkToast";
import { FeedbackToast } from "./FeedbackToast";
import {
  clearFeedbackTrigger,
  setFeedbackTrigger,
  useFeedbackTrigger,
} from "./feedbackTriggerStore";

/**
 * How long after landing in a spark the mount-driven moments fire. Long enough
 * that the page has settled and the user has looked at it, short enough to still
 * belong to the same visit.
 */
const MOUNT_TRIGGER_DELAY_MS = 5000;

/**
 * Shows at most one feedback form per visit, wherever the user is inside a
 * spark.
 *
 * Mounted once by the `SparkShell`, next to `ClassroomManagementOverlay`, so a
 * moment announced from any screen (`setFeedbackTrigger`) finds a renderer. The
 * API hands out every form this user still owes an answer to for this workshop,
 * already ordered oldest-first; the trigger decides which of them is shown.
 *
 * The read is deliberately lazy: feedback is an after-the-fact extra, so a slow
 * or failing request degrades to "no form" instead of suspending or breaking the
 * shell around it.
 */
export const FeedbackFormHost = ({
  workshop,
}: {
  workshop: WorkshopStepInfo;
}) => {
  const [forms] = useMultiModel([MODELS.WORKSHOP_FEEDBACK_FORMS(workshop.id)], {
    lazy: true,
  });
  const trigger = useFeedbackTrigger();
  const [searchParams] = useSearchParams();
  const hasTourParam = searchParams.has("tour");

  const [doDismiss] = useMutation(MUTATIONS.dismissFeedbackForm);
  const [doClickLink] = useMutation(MUTATIONS.clickFeedbackFormLink);
  const [doSubmit, { isLoading: isSubmitting }] = useMutation(
    MUTATIONS.submitFeedbackForm,
  );

  // Plain booleans rather than the array itself: a background refetch hands back
  // a new array on every revalidation, which would restart the timer below.
  const hasRegistrationForm = Boolean(
    forms?.some((candidate) => candidate.trigger === "afterRegistration"),
  );
  const hasCreationForm = Boolean(
    forms?.some((candidate) => candidate.trigger === "sparksCreation"),
  );

  // The two moments nobody else can announce. `afterRegistration` has no event
  // of its own — the API only offers those forms while the account is hours old,
  // so arriving in a spark at all is the signal. `sparksCreation` normally comes
  // from the user tour finishing; without `?tour` there is no tour to wait for.
  //
  // NEITHER fires while `?tour` is in the url: a toast on top of the running
  // tour interrupts it, and being the first trigger of the visit it would also
  // swallow the `sparksCreation` the tour announces when it ends.
  const wantsMountTrigger =
    !hasTourParam && (hasRegistrationForm || hasCreationForm);

  // At most one timer per visit. Answering or dismissing a form refetches the
  // list without it, which flips the flags above — without this the effect
  // would re-arm and open a second toast five seconds after the user closed
  // the first one.
  const mountTriggerFired = useRef(false);
  useEffect(() => {
    if (!wantsMountTrigger || mountTriggerFired.current) return;

    const timeout = setTimeout(() => {
      mountTriggerFired.current = true;
      setFeedbackTrigger(
        hasRegistrationForm ? "afterRegistration" : "sparksCreation",
      );
    }, MOUNT_TRIGGER_DELAY_MS);
    return () => clearTimeout(timeout);
  }, [wantsMountTrigger, hasRegistrationForm]);

  const form = trigger
    ? forms?.find((candidate) => candidate.trigger === trigger)
    : undefined;

  // A trigger with nothing to show must not hold the slot: the store keeps the
  // first trigger of a visit and ignores later ones, so a showroom visit with no
  // showroom form would otherwise swallow the upload moment that follows it.
  const hasUnusedTrigger = Boolean(trigger) && Boolean(forms) && !form;
  useEffect(() => {
    if (hasUnusedTrigger) clearFeedbackTrigger();
  }, [hasUnusedTrigger]);

  if (!form) return null;

  const handleDismiss = () => {
    void doDismiss({ workshopId: workshop.id, formId: form.id })?.catch(
      (e: Error) => {
        // Nothing to recover: the toast is already gone either way.
        console.error(`Failed to dismiss feedback form: ${e.message}`);
      },
    );
    clearFeedbackTrigger();
  };

  const handleSubmit = (answers: FeedbackAnswers) => {
    void doSubmit({ workshopId: workshop.id, formId: form.id, answers })
      ?.then(() => clearFeedbackTrigger())
      // Kept open on failure so the answers aren't silently thrown away — the
      // user can send again, or close it and record a dismissal.
      .catch((e: Error) => {
        console.error(`Failed to submit feedback form: ${e.message}`);
      });
  };

  const handleFollowLink = () => {
    void doClickLink({ workshopId: workshop.id, formId: form.id })?.catch(
      (e: Error) => {
        // The linked page is already open in its own tab; nothing to recover.
        console.error(`Failed to record feedback link click: ${e.message}`);
      },
    );
    clearFeedbackTrigger();
  };

  return (
    <ToastProvider>
      {form.mode === "link" ? (
        <FeedbackLinkToast
          key={form.id}
          form={form}
          onDismiss={handleDismiss}
          onFollow={handleFollowLink}
        />
      ) : (
        <FeedbackToast
          key={form.id}
          form={form}
          isSubmitting={isSubmitting}
          onDismiss={handleDismiss}
          onSubmit={handleSubmit}
        />
      )}
    </ToastProvider>
  );
};
